import { useCallback, useEffect, useRef, useState } from "react";

import { personalDocumentApi, type PersonalUploadTicket } from "@/services/personalDocuments";

export type PersonalUploadPhase =
  | "waiting"
  | "uploading"
  | "finalizing"
  | "queued"
  | "failed"
  | "cancelled";

export interface PersonalUploadQueueInput {
  file: File;
}

export interface PersonalUploadQueueItem extends PersonalUploadQueueInput {
  clientId: string;
  documentListId?: string;
  documentId?: string;
  phase: PersonalUploadPhase;
  progress: number;
  error?: string;
  failedAt?: "initiate" | "upload" | "complete";
}

interface UsePersonalDocumentUploadQueueOptions {
  concurrency?: number;
  onChanged?: () => void | Promise<void>;
}

function createClientId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function errorMessage(error: unknown) {
  const value = error as any;
  return String(
    value?.response?.data?.message ??
      value?.response?.data?.Message ??
      value?.message ??
      "Upload failed",
  );
}

function s3Endpoint(uploadUrl: string) {
  try {
    return new URL(uploadUrl).host;
  } catch {
    return "object storage";
  }
}

function s3HttpError(xhr: XMLHttpRequest, uploadUrl: string) {
  const body = String(xhr.responseText ?? "");
  const code = body.match(/<Code>([^<]+)<\/Code>/i)?.[1];
  const message = body.match(/<Message>([^<]+)<\/Message>/i)?.[1];
  const detail = [code, message].filter(Boolean).join(": ");
  return `S3 upload failed (${xhr.status}, ${s3Endpoint(uploadUrl)})${detail ? `: ${detail}` : ""}`;
}

function s3NetworkError(uploadUrl: string) {
  try {
    const target = new URL(uploadUrl);
    if (window.location.protocol === "https:" && target.protocol === "http:") {
      return `S3 upload blocked as mixed content (${target.host} uses HTTP)`;
    }
    if (navigator.onLine === false) return "S3 upload failed: browser is offline";
    return `S3 network/CORS error (${target.host}); no HTTP response was available`;
  } catch {
    return "S3 network/CORS error; no HTTP response was available";
  }
}

export function usePersonalDocumentUploadQueue({
  concurrency = 3,
  onChanged,
}: UsePersonalDocumentUploadQueueOptions) {
  const [items, setItems] = useState<PersonalUploadQueueItem[]>([]);
  const itemsRef = useRef(items);
  const xhrByClientId = useRef(new Map<string, XMLHttpRequest>());
  const cancelledClientIds = useRef(new Set<string>());
  itemsRef.current = items;

  useEffect(
    () => () => {
      xhrByClientId.current.forEach((xhr) => xhr.abort());
      xhrByClientId.current.clear();
    },
    [],
  );

  const patchItem = useCallback((clientId: string, patch: Partial<PersonalUploadQueueItem>) => {
    setItems((current) =>
      current.map((item) => (item.clientId === clientId ? { ...item, ...patch } : item)),
    );
  }, []);

  const putToS3 = useCallback(
    (item: PersonalUploadQueueItem, ticket: PersonalUploadTicket) =>
      new Promise<string | null>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrByClientId.current.set(item.clientId, xhr);
        xhr.open("PUT", ticket.uploadUrl, true);
        Object.entries(ticket.headers ?? {}).forEach(([name, value]) =>
          xhr.setRequestHeader(name, value),
        );
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          patchItem(item.clientId, {
            phase: "uploading",
            progress: Math.min(99, Math.round((event.loaded / event.total) * 100)),
          });
        };
        xhr.onerror = () => reject(new Error(s3NetworkError(ticket.uploadUrl)));
        xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
        xhr.ontimeout = () =>
          reject(new Error(`S3 upload timed out (${s3Endpoint(ticket.uploadUrl)})`));
        xhr.onload = () => {
          xhrByClientId.current.delete(item.clientId);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.getResponseHeader("ETag"));
          } else {
            reject(new Error(s3HttpError(xhr, ticket.uploadUrl)));
          }
        };
        xhr.send(item.file);
      }),
    [patchItem],
  );

  const executeTicket = useCallback(
    async (item: PersonalUploadQueueItem, ticket: PersonalUploadTicket) => {
      if (cancelledClientIds.current.has(item.clientId)) {
        patchItem(item.clientId, { phase: "cancelled", error: undefined });
        return;
      }
      patchItem(item.clientId, {
        documentListId: ticket.documentListId,
        documentId: ticket.documentId,
        phase: "uploading",
        progress: 0,
        error: undefined,
        failedAt: undefined,
      });
      let failedAt: PersonalUploadQueueItem["failedAt"] = "upload";
      try {
        const etag = await putToS3(item, ticket);
        patchItem(item.clientId, { phase: "finalizing", progress: 100 });
        failedAt = "complete";
        await personalDocumentApi.complete(ticket.documentListId, etag);
        patchItem(item.clientId, { phase: "queued", progress: 100 });
      } catch (error) {
        if ((error as any)?.name === "AbortError") {
          patchItem(item.clientId, { phase: "cancelled", error: undefined });
        } else {
          patchItem(item.clientId, {
            phase: "failed",
            error: errorMessage(error),
            failedAt,
          });
        }
      } finally {
        xhrByClientId.current.delete(item.clientId);
        await onChanged?.();
      }
    },
    [onChanged, patchItem, putToS3],
  );

  const upload = useCallback(
    async (inputs: PersonalUploadQueueInput[]) => {
      if (inputs.length === 0) return;
      const queued = inputs.map<PersonalUploadQueueItem>((input) => ({
        ...input,
        clientId: createClientId(),
        phase: "waiting",
        progress: 0,
      }));
      setItems((current) => [...queued, ...current]);
      try {
        const initiated = await personalDocumentApi.initiate(
          queued.map((item) => ({ clientId: item.clientId, file: item.file })),
        );
        initiated.rejected.forEach((rejected) =>
          patchItem(rejected.clientId, {
            phase: "failed",
            error: rejected.error,
            failedAt: "initiate",
          }),
        );
        initiated.accepted.forEach((ticket) =>
          patchItem(ticket.clientId, {
            documentListId: ticket.documentListId,
            documentId: ticket.documentId,
          }),
        );
        await onChanged?.();

        const queue = initiated.accepted.map((ticket) => ({
          ticket,
          item: queued.find((candidate) => candidate.clientId === ticket.clientId)!,
        }));
        let cursor = 0;
        const worker = async () => {
          while (cursor < queue.length) {
            const current = queue[cursor++];
            if (!current?.item) continue;
            if (cancelledClientIds.current.has(current.item.clientId)) {
              patchItem(current.item.clientId, { phase: "cancelled", error: undefined });
              continue;
            }
            await executeTicket(current.item, current.ticket);
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(Math.max(1, concurrency), queue.length) }, () => worker()),
        );
      } catch (error) {
        queued.forEach((item) =>
          patchItem(item.clientId, {
            phase: "failed",
            error: errorMessage(error),
            failedAt: "initiate",
          }),
        );
      }
    },
    [concurrency, executeTicket, onChanged, patchItem],
  );

  const cancel = useCallback(
    (clientId: string) => {
      cancelledClientIds.current.add(clientId);
      xhrByClientId.current.get(clientId)?.abort();
      if (!xhrByClientId.current.has(clientId)) {
        patchItem(clientId, { phase: "cancelled", error: undefined });
      }
    },
    [patchItem],
  );

  const retry = useCallback(
    async (clientId: string) => {
      const item = itemsRef.current.find((candidate) => candidate.clientId === clientId);
      if (!item) return;
      cancelledClientIds.current.delete(clientId);
      patchItem(clientId, { phase: "waiting", progress: 0, error: undefined });
      try {
        if (!item.documentListId || item.failedAt === "initiate") {
          const initiated = await personalDocumentApi.initiate([
            { clientId: item.clientId, file: item.file },
          ]);
          const rejected = initiated.rejected[0];
          if (rejected) {
            patchItem(clientId, {
              phase: "failed",
              error: rejected.error,
              failedAt: "initiate",
            });
            return;
          }
          const ticket = initiated.accepted[0];
          if (!ticket) throw new Error("Upload retry was not accepted");
          await onChanged?.();
          await executeTicket(item, ticket);
          return;
        }
        if (item.failedAt === "complete") {
          let retryError: unknown;
          try {
            await personalDocumentApi.retry(item.documentListId);
            patchItem(clientId, { phase: "queued", progress: 100, failedAt: undefined });
            await onChanged?.();
            return;
          } catch (error) {
            retryError = error;
            // S3 完成校验失败时没有可重试的 PIKE job，回退为重新签发 PUT。
          }
          try {
            const ticket = await personalDocumentApi.renew(item.documentListId);
            await executeTicket(item, ticket);
            return;
          } catch (renewError) {
            throw retryError ?? renewError;
          }
        }
        const ticket = await personalDocumentApi.renew(item.documentListId);
        await executeTicket(item, ticket);
      } catch (error) {
        patchItem(clientId, {
          phase: "failed",
          error: errorMessage(error),
          failedAt: item.failedAt ?? "upload",
        });
      }
    },
    [executeTicket, onChanged, patchItem],
  );

  return { items, upload, cancel, retry };
}
